import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, MessageSquare } from 'lucide-react';
import { PHONES, CONTACT, DISTRICTS } from '../content';
import PageHero from '../components/PageHero';
import LiveBadge from '../components/LiveBadge';
import { SEOHead } from '../components/SEOHead';

const WA_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

const CONTACT_CHANNELS = [
  {
    icon: Phone, label: 'Ana Hat', value: PHONES.main, href: PHONES.main_tel,
    color: 'var(--red)', bg: 'rgba(229,51,51,.12)', borderColor: 'rgba(229,51,51,.25)', primary: true,
  },
  {
    icon: Phone, label: 'Hat 2', value: PHONES.secondary, href: PHONES.secondary_tel,
    color: 'var(--blue)', bg: 'var(--blue-dim)', borderColor: 'var(--blue-border)',
  },
  {
    icon: Phone, label: 'Hat 3', value: PHONES.tertiary, href: PHONES.tertiary_tel,
    color: 'var(--blue)', bg: 'var(--blue-dim)', borderColor: 'var(--blue-border)',
  },
];

export default function Iletisim() {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:${CONTACT.email}?subject=Lastik Yol Yardım Talebi - ${form.name}&body=${encodeURIComponent(`Ad Soyad: ${form.name}\nTelefon: ${form.phone}\n\nMesaj:\n${form.message}`)}`;
    window.location.href = mailtoLink;
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <main data-testid="iletisim-page" style={{ background: 'var(--bg)' }}>
      <SEOHead path="/iletisim" />
      <PageHero
        breadcrumb="İletişim"
        headline="Bize Ulaşın."
        accentText="Hemen."
        sub="Acil mi? Telefon en hızlısı. Genel soru mu? Formu kullanın."
        badge="3 Aktif Hat"
        bgImage="https://lh3.googleusercontent.com/gps-cs-s/AHVAweq3XuoJ81PkgmF7TJftSdhOISWb-WV4AN5RY1shOKS0IIzvOAe94U3wiO8VxRophMBOTjAEwcQClVc9QL5MzI9JYuXa0fAY8gkxZxByGNt080lEw3-a8ak_X1USG1wcuC-hT6GQK6B70q1W=s1360-w1360-h1020-rw"
      />

      {/* ─── CONTENT ─── */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr', gap: '3rem', alignItems: 'start' }} className="iletisim-grid">
            {/* Left — Contact Info */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: .5 }}>
              <h3 style={{
                fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                fontSize: '1.15rem', color: 'var(--text)', marginBottom: '1rem',
              }}>Telefon Hatları</h3>

              {CONTACT_CHANNELS.map(({ icon: Icon, label, value, href, color, bg, borderColor, primary }) => (
                <a key={href} href={href} data-testid="contact-phone-link" style={{ textDecoration: 'none', display: 'block', marginBottom: '.75rem' }}>
                  <div style={{
                    background: primary ? 'linear-gradient(135deg, rgba(229,51,51,.06) 0%, var(--card) 100%)' : 'var(--card)',
                    border: `1px solid ${primary ? borderColor : 'var(--border)'}`,
                    borderRadius: 'var(--rl)', padding: '1rem 1.25rem',
                    display: 'flex', alignItems: 'center', gap: '.875rem',
                    transition: 'border-color .2s, background .2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.background = 'var(--card2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = primary ? borderColor : 'var(--border)'; e.currentTarget.style.background = primary ? 'linear-gradient(135deg, rgba(229,51,51,.06) 0%, var(--card) 100%)' : 'var(--card)'; }}
                  >
                    <div style={{
                      width: 42, height: 42, borderRadius: 11,
                      background: bg, border: `1px solid ${borderColor}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: color, flexShrink: 0,
                    }}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text2)', marginBottom: '.15rem' }}>{label}</div>
                      <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '1.05rem', color: 'var(--text)' }}>{value}</div>
                    </div>
                    {primary && <LiveBadge text="Aktif" size="small" style={{ marginLeft: 'auto' }} />}
                  </div>
                </a>
              ))}

              {/* WhatsApp */}
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block', marginBottom: '.75rem' }}
                data-gtm-action="whatsapp_click">
                <div style={{
                  background: 'var(--green-dim)', border: '1px solid var(--green-border)',
                  borderRadius: 'var(--rl)', padding: '1rem 1.25rem',
                  display: 'flex', alignItems: 'center', gap: '.875rem',
                  transition: 'background .2s',
                }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 11,
                    background: 'rgba(16,185,129,.15)', border: '1px solid var(--green-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--green)', flexShrink: 0,
                  }}>{WA_ICON}</div>
                  <div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text2)', marginBottom: '.15rem' }}>WhatsApp</div>
                    <div style={{ fontWeight: 600, fontSize: '.95rem', color: 'var(--green)' }}>Mesaj Gönder</div>
                  </div>
                </div>
              </a>

              {/* Email */}
              <a href={`mailto:${CONTACT.email}`} style={{ textDecoration: 'none', display: 'block', marginBottom: '.75rem' }}>
                <div className="ci">
                  <div className="ci-icon"><Mail size={18} /></div>
                  <div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text2)', marginBottom: '.15rem' }}>E-Posta</div>
                    <div style={{ fontWeight: 600, fontSize: '.93rem', color: 'var(--text)' }}>{CONTACT.email}</div>
                  </div>
                </div>
              </a>

              {/* Hours */}
              <div className="ci" style={{ marginBottom: '.75rem' }}>
                <div className="ci-icon" style={{ background: 'rgba(16,185,129,.12)', border: '1px solid var(--green-border)', color: 'var(--green)' }}>
                  <Clock size={18} />
                </div>
                <div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text2)', marginBottom: '.15rem' }}>Çalışma Saatleri</div>
                  <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: '1rem' }}>7/24 — 365 Gün Açık</div>
                  <div style={{ fontSize: '.75rem', color: 'var(--text2)' }}>Cumartesi, Pazar ve tatil günleri dahil</div>
                </div>
              </div>

              {/* Coverage */}
              <div className="ci" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '.75rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
                  <div className="ci-icon"><MapPin size={18} /></div>
                  <div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text2)', marginBottom: '.15rem' }}>Hizmet Bölgesi</div>
                    <div style={{ fontWeight: 600, fontSize: '.93rem', color: 'var(--text)' }}>Kocaeli — {DISTRICTS.length} İlçe</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.35rem' }}>
                  {DISTRICTS.map(d => (
                    <span key={d} className="chip" style={{ fontSize: '.72rem', padding: '.2rem .6rem' }}>{d}</span>
                  ))}
                </div>
              </div>

              {/* Map */}
              <div className="map-wrap" data-testid="contact-map-iframe" style={{ height: 280 }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d192697.77967960754!2d29.70997539562064!3d40.776044208697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cb8f5f8bc7bc8b%3A0x3fb5ac5f3b69219e!2sKocaeli!5e0!3m2!1str!2str!4v1705312547892!5m2!1str!2str"
                  title="Kocaeli Haritası"
                  allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>

            {/* Right — Form */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: .5, delay: .1 }}>
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--rxl)', padding: '2.5rem',
                position: 'sticky', top: 100,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.625rem', marginBottom: '.5rem' }}>
                  <MessageSquare size={20} style={{ color: 'var(--blue)' }} />
                  <h2 style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                    fontSize: '1.25rem', color: 'var(--text)',
                  }}>Mesaj Gönderin</h2>
                </div>
                <p style={{ color: 'var(--text2)', fontSize: '.85rem', marginBottom: '1.75rem', lineHeight: 1.6 }}>
                  Acil durumlarda lütfen telefon ile ulaşın. Genel sorularınız için formu kullanabilirsiniz.
                </p>

                {sent ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--green)' }}>
                    <CheckCircle size={48} style={{ margin: '0 auto 1rem', display: 'block' }} />
                    <p style={{ fontWeight: 600, fontSize: '1rem' }}>Mesajınız iletildi!</p>
                    <p style={{ fontSize: '.85rem', color: 'var(--text2)', marginTop: '.35rem' }}>E-posta istemciniz açıldı.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {[
                      { name: 'name', label: 'Ad Soyad', type: 'text', placeholder: 'Adınızı girin', testId: 'contact-name-input' },
                      { name: 'phone', label: 'Telefon Numarası', type: 'tel', placeholder: '05xx xxx xx xx', testId: 'contact-phone-input' },
                    ].map(field => (
                      <div key={field.name}>
                        <label style={{ display: 'block', fontSize: '.8rem', color: 'var(--text2)', marginBottom: '.4rem', fontWeight: 500 }}>
                          {field.label} *
                        </label>
                        <input
                          data-testid={field.testId}
                          type={field.type}
                          required
                          placeholder={field.placeholder}
                          value={form[field.name]}
                          onChange={e => setForm({ ...form, [field.name]: e.target.value })}
                          style={{
                            width: '100%', background: 'var(--card)',
                            border: '1px solid var(--border)', borderRadius: 'var(--r)',
                            padding: '.75rem 1rem', color: 'var(--text)',
                            fontSize: '.85rem', outline: 'none', transition: 'border-color .2s',
                          }}
                          onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                          onBlur={e => e.target.style.borderColor = 'var(--border)'}
                        />
                      </div>
                    ))}
                    <div>
                      <label style={{ display: 'block', fontSize: '.8rem', color: 'var(--text2)', marginBottom: '.4rem', fontWeight: 500 }}>
                        Mesajınız *
                      </label>
                      <textarea
                        data-testid="contact-message-input"
                        required rows={5}
                        placeholder="Lastik sorununuzu veya sorunuzu yazın..."
                        value={form.message}
                        onChange={e => setForm({ ...form, message: e.target.value })}
                        style={{
                          width: '100%', background: 'var(--card)',
                          border: '1px solid var(--border)', borderRadius: 'var(--r)',
                          padding: '.75rem 1rem', color: 'var(--text)',
                          fontSize: '.85rem', outline: 'none', resize: 'vertical',
                          transition: 'border-color .2s', fontFamily: 'inherit',
                        }}
                        onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                        onBlur={e => e.target.style.borderColor = 'var(--border)'}
                      />
                    </div>

                    <button
                      data-testid="contact-submit"
                      type="submit"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.6rem',
                        background: 'var(--blue)', color: '#fff', borderRadius: 'var(--r)',
                        padding: '.875rem', fontWeight: 700, fontSize: '.93rem',
                        border: 'none', cursor: 'pointer', transition: 'background .2s', width: '100%',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--blue)'}
                    >
                      <Send size={15} /> Mesaj Gönder
                    </button>

                    <p style={{ fontSize: '.75rem', color: 'var(--muted)', textAlign: 'center', lineHeight: 1.5 }}>
                      Acil durumlar için lütfen doğrudan arayın:{' '}
                      <a href={PHONES.main_tel} style={{ color: 'var(--red)', fontWeight: 600, textDecoration: 'none' }}>{PHONES.main}</a>
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <style>{`
        @media(max-width:768px){
          .iletisim-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </main>
  );
}
